import React, { type PropsWithChildren, type ComponentPropsWithoutRef } from 'react';
import castArray from 'lodash/castArray';
import { Box, xcss, type XCSS } from '@atlaskit/primitives';
import { ff } from '@atlassian/jira-feature-flagging';

export type Props = PropsWithChildren<{
	/**
	 * Optional border styles to apply to the `td` element. If not provided, no borders will be rendered.
	 */
	border?: XCSS | Array<XCSS | false | undefined>;
	/**
	 * Optional spacing styles to apply to the `td` element. If not provided, default spacing will apply.
	 */
	spacing?: XCSS | Array<XCSS | false | undefined>;
	/**
	 * @deprecated - Cleanup with TODO: Cleanup with nin-resizable-columns_8z4eo
	 * Column width to apply to the `td` element.
	 */
	columnWidth?: number;
	/**
	 * @deprecated - Cleanup with TODO: Cleanup with nin-resizable-columns_8z4eo
	 * Flag to indicate whether the column width should be flexible or fixed.
	 */
	isFlexWidth?: boolean;
}> &
	ComponentPropsWithoutRef<'td'>;

export const TableCell = ({
	children,
	border,
	spacing,
	columnWidth,
	isFlexWidth,
	...htmlAttributes
}: Props) => {
	const baseStyles = ff('jsv-one-issue-table-spreadsheet-ui_ygrnf')
		? redesignBaseStyles
		: oldBaseStyles;

	const maxWidthStyles = columnWidth && !isFlexWidth ? getMaxWidthStyles(columnWidth) : undefined;

	const defaultSpacingStyles = ff('jsv-one-issue-table-spreadsheet-ui_ygrnf')
		? redesignSpacingStyles
		: oldSpacingStyles;

	const spacingStyles = spacing !== undefined ? castArray(spacing) : [defaultSpacingStyles];

	const borderStyles =
		ff('jsv-one-issue-table-spreadsheet-ui_ygrnf') && border !== undefined ? castArray(border) : [];

	return (
		<Box
			as="td"
			xcss={[
				// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
				baseStyles,
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				ff('nin-resizable-columns_8z4eo') && relativeStyles,
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
				!ff('nin-resizable-columns_8z4eo') && maxWidthStyles,
				...spacingStyles,
				...borderStyles,
			]}
			// eslint-disable-next-line react/jsx-props-no-spreading
			{...htmlAttributes}
		>
			{children}
		</Box>
	);
};

const oldBaseStyles = xcss({
	borderSpacing: 0,
	border: 'none',
	// Cross-browser CSS hack to allow inline editable fields to fill the entire space of their `td`. The
	// preferred long-term solution is to adopt a flexbox layout, but until then, please do extensive
	// cross-browser testing before you update this rule.
	height: '100%',
});

const redesignBaseStyles = xcss({
	background: 'inherit',
	// Cross-browser CSS hack to allow inline editable fields to fill the entire space of their `td`. The
	// preferred long-term solution is to adopt a flexbox layout, but until then, please do extensive
	// cross-browser testing before you update this rule.
	height: '100%',
});

const relativeStyles = xcss({
	position: 'relative',
});

const oldSpacingStyles = xcss({
	padding: 'space.100',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	':first-of-type': {
		paddingLeft: 'space.100',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	':last-of-type': {
		paddingRight: 'space.100',
	},
});

const redesignSpacingStyles = xcss({
	paddingBlock: 'space.050',
	paddingInline: 'space.100',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	':first-of-type': {
		paddingLeft: 'space.100',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	':last-of-type': {
		paddingRight: 'space.100',
	},
});

const getMaxWidthStyles = (columnWidth: number) =>
	xcss({
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		maxWidth: `${columnWidth}px`,
	});
